import { Injectable, OnDestroy } from '@angular/core';

interface ICacheGirdi<T> {
  veri: T;
  zaman: number;
  ttl: number;
}

export interface ISWRSonuc<T> {
  veri: T | null;
  bayat: boolean;
}

@Injectable({ providedIn: 'root' })
export class UnifiedCacheService implements OnDestroy {
  private readonly store = new Map<string, ICacheGirdi<any>>();
  private readonly bekleyenler = new Map<string, Promise<any>>();
  private readonly VARSAYILAN_TTL = 60_000;
  private readonly BAKIM_ARALIĞI = 30_000;
  private readonly MAX_BOYUT = 5000;
  private bakimTimer: any;

  constructor() {
    this.bakimTimer = setInterval(() => this.bakim(), this.BAKIM_ARALIĞI);
  }

  ngOnDestroy(): void {
    if (this.bakimTimer) {
      clearInterval(this.bakimTimer);
      this.bakimTimer = null;
    }
  }

  /**
   * SWR (Stale-While-Revalidate) cache okuma.
   * TTL icinde → taze veri.
   * TTL-2xTTL arasi → bayat veri (arka planda yenile).
   * 3xTTL sonrasi → hard expire, null doner.
   */
  get<T>(anahtar: string, ttl?: number): ISWRSonuc<T> {
    const girdi = this.store.get(anahtar);
    if (!girdi) return { veri: null, bayat: false };

    const yas = Date.now() - girdi.zaman;
    const etkinTTL = ttl ?? girdi.ttl;

    // 3xTTL = hard expire
    if (yas > etkinTTL * 3) {
      this.store.delete(anahtar);
      return { veri: null, bayat: false };
    }

    // 2xTTL = stale window sonu (eski SWR uyumu)
    if (yas > etkinTTL * 2) {
      this.store.delete(anahtar);
      return { veri: null, bayat: false };
    }

    return {
      veri: girdi.veri as T,
      bayat: yas > etkinTTL,
    };
  }

  /** Basit cache okuma (SWR olmadan, bayat veriyi dondurme) */
  al<T>(anahtar: string, ttl?: number): T | null {
    const sonuc = this.get<T>(anahtar, ttl);
    if (sonuc.bayat) return null;
    return sonuc.veri;
  }

  /** Cache'e yaz */
  set<T>(anahtar: string, veri: T, ttl?: number): T {
    if (this.store.size >= this.MAX_BOYUT) {
      // En eski entry'yi sil (ilk key - Map insertion order)
      const ilkAnahtar = this.store.keys().next().value;
      if (ilkAnahtar) this.store.delete(ilkAnahtar);
    }
    this.store.set(anahtar, {
      veri,
      zaman: Date.now(),
      ttl: ttl ?? this.VARSAYILAN_TTL,
    });
    return veri;
  }

  /** Tekil anahtar sil */
  sil(anahtar: string): void {
    this.store.delete(anahtar);
  }

  /** Prefix bazli veya tum cache temizle */
  temizle(onek?: string): void {
    if (!onek) {
      this.store.clear();
      return;
    }
    for (const key of this.store.keys()) {
      if (key.startsWith(onek)) this.store.delete(key);
    }
  }

  /**
   * Request dedup: Ayni anda yapilan ayni istekleri tek promise'e birlestirir.
   * Birden fazla bilesen ayni veriyi ayni anda isterse, tek bir API cagrisi yapilir.
   */
  async dedup<T>(anahtar: string, istek: () => Promise<T>): Promise<T> {
    const mevcut = this.bekleyenler.get(anahtar);
    if (mevcut) return mevcut as Promise<T>;

    const promise = istek().finally(() => this.bekleyenler.delete(anahtar));
    this.bekleyenler.set(anahtar, promise);
    return promise;
  }

  /** Suresi dolmus girdileri temizle */
  bakim(): void {
    const simdi = Date.now();
    for (const [anahtar, girdi] of this.store) {
      if (simdi - girdi.zaman > girdi.ttl * 3) {
        this.store.delete(anahtar);
      }
    }
  }

  /** Bekleyen dedup istek sayisi (debug/monitoring) */
  get bekleyenSayisi(): number {
    return this.bekleyenler.size;
  }

  /** Toplam cache girdi sayisi (debug/monitoring) */
  get boyut(): number {
    return this.store.size;
  }
}
